import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  buildingServicesApi,
  CreateEntryExitRegistrationRequest,
  EntryExitRegistration,
  BuildingServiceSettings,
} from '../../api/buildingServicesApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { resolveDefaultBuildingId, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps } from '../../utils/buildingSelection';
import {
  advanceBookingWindowError,
  buildAdvanceBookingIsoDateBounds,
  resolveServiceAdvanceBookingDays,
} from '../../utils/advance-booking-days.util';

const AddEditEntryExitRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEditing = !!id;

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [serviceSettings, setServiceSettings] = useState<BuildingServiceSettings | null>(null);
  const [registration, setRegistration] = useState<EntryExitRegistration | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    apartmentId: '',
    visitorName: '',
    visitorPhone: '',
    visitDate: '',
    startTime: '',
    endTime: '',
    purpose: '',
    notes: '',
    agreeToRegulations: false,
  });

  useEffect(() => { loadBuildings(); }, []);
  useEffect(() => {
    if (isEditing && buildings.length > 0) loadRegistration();
  }, [isEditing, id, buildings.length]);
  useEffect(() => {
    if (selectedBuilding) {
      loadApartments();
      loadServiceSettings();
    }
  }, [selectedBuilding]);
  useEffect(() => {
    if (['STAFF', 'SUPERADMIN', 'OPERATOR'].includes(user?.role || '')) {
      setFormData((prev) => ({ ...prev, agreeToRegulations: true }));
    }
  }, [user?.role]);

  const advanceBookingBounds = useMemo(
    () => resolveServiceAdvanceBookingDays(serviceSettings, user?.role),
    [serviceSettings, user?.role],
  );
  const { minDate, maxDate } = useMemo(
    () => buildAdvanceBookingIsoDateBounds(advanceBookingBounds),
    [advanceBookingBounds],
  );
  const allowNegativeMin = user?.role !== 'RESIDENT';

  const loadBuildings = async () => {
    const response = await buildingsApi.getBuildings();
    setBuildings(response);
    const buildingIdFromUrl = searchParams.get('buildingId');
    const defaultId = buildingIdFromUrl || resolveDefaultBuildingId(response, user);
    if (defaultId) {
      const buildingToSelect = response.find((b) => b.id === defaultId);
      if (buildingToSelect) setSelectedBuilding(buildingToSelect);
    }
  };

  const loadApartments = async () => {
    if (!selectedBuilding) return;
    const response = await apartmentsApi.getApartments();
    setApartments(response.filter((apt) => apt.buildingId === selectedBuilding.id));
  };

  const loadServiceSettings = async () => {
    if (!selectedBuilding) return;
    const settings = await buildingServicesApi.getServiceSettings(selectedBuilding.id, 'ENTRY_EXIT');
    setServiceSettings(settings);
  };

  const loadRegistration = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await buildingServicesApi.getEntryExitRegistration(id);
      setRegistration(data);
      const building = buildings.find((b) => b.id === data.buildingId);
      if (building) setSelectedBuilding(building);
      setFormData({
        apartmentId: data.apartmentId,
        visitorName: data.visitorName,
        visitorPhone: data.visitorPhone,
        visitDate: data.visitDate.split('T')[0],
        startTime: data.startTime,
        endTime: data.endTime,
        purpose: data.purpose,
        notes: data.notes || '',
        agreeToRegulations: data.agreeToRegulations,
      });
    } catch {
      toast.error('Lỗi khi tải thông tin đăng ký');
      navigate('/building-services/entry-exit');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.apartmentId) newErrors.apartmentId = 'Vui lòng chọn căn hộ';
    if (!formData.visitorName.trim()) newErrors.visitorName = 'Vui lòng nhập tên khách';
    if (!formData.visitorPhone.trim()) newErrors.visitorPhone = 'Vui lòng nhập số điện thoại';
    if (!formData.visitDate) {
      newErrors.visitDate = 'Vui lòng chọn ngày đến';
    } else {
      const windowError = advanceBookingWindowError(formData.visitDate, advanceBookingBounds, {
        allowNegativeMin,
      });
      if (windowError) newErrors.visitDate = windowError;
    }
    if (!formData.startTime) newErrors.startTime = 'Vui lòng chọn giờ bắt đầu';
    if (!formData.endTime) newErrors.endTime = 'Vui lòng chọn giờ kết thúc';
    if (!formData.purpose.trim()) newErrors.purpose = 'Vui lòng nhập mục đích';
    if (!formData.agreeToRegulations && user?.role === 'RESIDENT') {
      newErrors.agreeToRegulations = 'Vui lòng đồng ý với quy định';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return toast.error('Vui lòng kiểm tra lại thông tin');
    setSubmitting(true);
    try {
      const requestData: CreateEntryExitRegistrationRequest = {
        ...formData,
        visitorName: formData.visitorName.trim(),
        visitorPhone: formData.visitorPhone.trim(),
        purpose: formData.purpose.trim(),
        notes: formData.notes.trim() || undefined,
        agreeToRegulations: formData.agreeToRegulations || ['STAFF', 'SUPERADMIN', 'OPERATOR'].includes(user?.role || ''),
      };
      if (isEditing && id) {
        await buildingServicesApi.updateEntryExitRegistration(id, requestData);
        toast.success('Cập nhật đăng ký thành công');
      } else {
        await buildingServicesApi.createEntryExitRegistration(requestData);
        toast.success('Tạo đăng ký thành công');
      }
      navigate('/building-services/entry-exit');
    } catch {
      toast.error('Lỗi khi lưu đăng ký');
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = isEditing && ['COMPLETED', 'CANCELLED', 'REJECTED'].includes(registration?.status || '');

  if (loading) return <div className="p-8 text-center">Đang tải...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{isEditing ? 'Cập nhật đăng ký ra vào' : 'Thêm đăng ký ra vào'}</h1>
          <p className="text-gray-600">Đăng ký thông tin khách đến thăm</p>
        </div>
        <button onClick={() => navigate('/building-services/entry-exit')} className="text-gray-600">← Quay lại</button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
        {shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-sm font-medium mb-2">Tòa nhà *</label>
            <select
              value={selectedBuilding?.id || ''}
              onChange={(e) => {
                const building = buildings.find((b) => b.id === e.target.value);
                setSelectedBuilding(building || null);
                setFormData((prev) => ({ ...prev, apartmentId: '' }));
              }}
              disabled={isEditing || !needsBuildingSelection(buildings)}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Căn hộ *</label>
          <select
            value={formData.apartmentId}
            onChange={(e) => setFormData((prev) => ({ ...prev, apartmentId: e.target.value }))}
            disabled={isReadOnly || !selectedBuilding}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="">-- Chọn căn hộ --</option>
            {apartments.map((apt) => <option key={apt.id} value={apt.id}>{apt.code} - {apt.ownerName}</option>)}
          </select>
          {errors.apartmentId && <p className="text-red-600 text-sm mt-1">{errors.apartmentId}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tên khách *</label>
            <input value={formData.visitorName} onChange={(e) => setFormData((prev) => ({ ...prev, visitorName: e.target.value }))} disabled={isReadOnly} className="w-full border rounded-lg px-3 py-2" />
            {errors.visitorName && <p className="text-red-600 text-sm mt-1">{errors.visitorName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Số điện thoại *</label>
            <input value={formData.visitorPhone} onChange={(e) => setFormData((prev) => ({ ...prev, visitorPhone: e.target.value }))} disabled={isReadOnly} className="w-full border rounded-lg px-3 py-2" />
            {errors.visitorPhone && <p className="text-red-600 text-sm mt-1">{errors.visitorPhone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Ngày đến *</label>
            <input
              type="date"
              value={formData.visitDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, visitDate: e.target.value }))}
              disabled={isReadOnly}
              min={minDate}
              max={maxDate}
              className="w-full border rounded-lg px-3 py-2"
            />
            {errors.visitDate && <p className="text-red-600 text-sm mt-1">{errors.visitDate}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Giờ bắt đầu *</label>
            <input type="time" value={formData.startTime} onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))} disabled={isReadOnly} className="w-full border rounded-lg px-3 py-2" />
            {errors.startTime && <p className="text-red-600 text-sm mt-1">{errors.startTime}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Giờ kết thúc *</label>
            <input type="time" value={formData.endTime} onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))} disabled={isReadOnly} className="w-full border rounded-lg px-3 py-2" />
            {errors.endTime && <p className="text-red-600 text-sm mt-1">{errors.endTime}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Mục đích *</label>
          <textarea value={formData.purpose} onChange={(e) => setFormData((prev) => ({ ...prev, purpose: e.target.value }))} disabled={isReadOnly} rows={4} className="w-full border rounded-lg px-3 py-2" />
          {errors.purpose && <p className="text-red-600 text-sm mt-1">{errors.purpose}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Ghi chú</label>
          <textarea value={formData.notes} onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))} disabled={isReadOnly} rows={2} className="w-full border rounded-lg px-3 py-2" />
        </div>

        {serviceSettings?.regulations && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Quy định đăng ký ra vào</h3>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{serviceSettings.regulations}</pre>
            {user?.role === 'RESIDENT' && (
              <label className="flex items-start mt-4">
                <input type="checkbox" checked={formData.agreeToRegulations} onChange={(e) => setFormData((prev) => ({ ...prev, agreeToRegulations: e.target.checked }))} disabled={isReadOnly} className="mt-1" />
                <span className="ml-2 text-sm">Tôi đồng ý với quy định *</span>
              </label>
            )}
            {errors.agreeToRegulations && <p className="text-red-600 text-sm mt-1">{errors.agreeToRegulations}</p>}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/building-services/entry-exit')} className="px-6 py-2 border rounded-lg">Hủy</button>
          {!isReadOnly && (
            <button type="submit" disabled={submitting || serviceSettings?.isActive === false} className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              {submitting ? 'Đang lưu...' : isEditing ? 'Cập nhật' : 'Tạo đăng ký'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddEditEntryExitRegistration;
